
/*

 HELPFUL DOCUMENTATION FOR HOW JS-XLSX IS USED.

 Had to fork the https://github.com/protobi/js-xlsx version of this module
 to get style functionality when saving a workbook.

 ( Main Project: import XLSX from 'xlsx' )

 And then manually apply the fix in 'xlsx-browserify-shim' because of some dependency issues
 (https://github.com/SheetJS/js-xlsx/issues/143#issuecomment-154567695)

 ### Documentation for features we use for SB exports

 [https://github.com/protobi/js-xlsx#cell-styles](Reference Guide)

 - https://github.com/SheetJS/js-xlsx/pull/66/files#diff-a470cfa184451a7854f1edc917351da3
 - Writing Styles: https://github.com/SheetJS/js-xlsx/issues/128
 - Explains fgColor vs. bgColor: https://github.com/protobi/js-xlsx/issues/9#issuecomment-135577802
 - Cell Merge: https://github.com/SheetJS/js-xlsx/issues/41 (merged, and works; see below)

 - Style Fork: https://github.com/SheetJS/js-xlsx/issues/128#issuecomment-76243917

 ### Styles (copied from GH Readme)

 Key	Description
 v	  raw value (see Data Types section for more info)
 w	  formatted text (if applicable)
 t	  cell type: b Boolean, n Number, e error, s String, d Date
 f	  cell formula encoded as an A1-style string (if applicable)
 F	  range of enclosing array if formula is array formula (if applicable)
 r	  rich text encoding (if applicable)
 h	  HTML rendering of the rich text (if applicable)
 c	  comments associated with the cell
 z	  number format string associated with the cell (if requested)
 l	  cell hyperlink object (.Target holds link, .tooltip is tooltip)
 s	  the style/theme of the cell (if applicable)

 ### Dummies Guide to ARGB

 Transparency is controlled by the alpha channel (AA in #AARRGGBB).
 Maximal value (255 dec, FF hex) means fully opaque.
 Minimum value (0 dec, 00 hex) means fully transparent.
 Values in between are semi-transparent, i.e. the color is mixed with the background color.

 Here is the table of % to hex values E.g. For 50% white you would use #80FFFFFF.

 100% — FF
 95% — F2
 90% — E6
 85% — D9
 80% — CC
 75% — BF
 70% — B3
 65% — A6
 60% — 99
 55% — 8C
 50% — 80
 45% — 73
 40% — 66
 35% — 59
 30% — 4D
 25% — 40
 20% — 33
 15% — 26
 10% — 1A
 5% — 0D
 0% — 00

 ### For local development when patching our js-xlsx fork

 from js-xlsx: npm link (https://docs.npmjs.com/cli/link)
 from scoreboard/static: npm link xlsx (our fork)
 make any changes to js-xlsx
 from js-xlsx: make

*/

import _ from 'lodash'

import XLSX from 'xlsx'
import config from 'scoreboard/components/config'
import constants from 'scoreboard/components/constants'
import locale from 'scoreboard/components/locale'
import formatValue, {
	appendCurrencySymbol, appendPercentSign
} from 'scoreboard/components/format-value'
var group = formatValue.group
var round = formatValue.round
import moment from 'moment'
import momentHelper from 'scoreboard/components/moment-helper'
import DataType from 'scoreboard/datasets/model/data-type'
const { NUMBER, CURRENCY, PERCENT, TEXT, DATE, TIME, DATE_TIME } = DataType

// There are 72 points per inch; assume 96 pixels per inch
var PIXEL_POINT_CONVERSION_FACTOR = 72 / 96

/*
 * Excel has a default font size of 12, but we want 14 so the width needs to be
 * adjusted based on this ratio
 *
 * https://support.microsoft.com/en-us/help/214123/description-of-how-column-widths-are-determined-in-excel
 */
var FONT_SIZE = 14
var EXCEL_FONT_DEFAULT = 12
var FONT_SCALE_FACTOR = FONT_SIZE / EXCEL_FONT_DEFAULT
const CELL_PADDING = 35 // UI uses 15px on left/right margin, we need a tad more
const MIN_WIDTH_PIXELS = 20 + CELL_PADDING

// Excel Defaults
var MIN_COLUMN_WIDTH_EXCEL_POINTS = 8
var MIN_ROW_HEIGHT = 16
// Inspected in Excel for Mac
var ROW_HEIGHT_PER_LINE = 20

// Taken from xlsx (xlsx/bits/45_styutils.js) to do height conversions
// Best to use the same pixel -> character conversion
// Leaving naming as is for searchability
var DEF_MDW = 7, MAX_MDW = 15, MIN_MDW = 1, MDW = DEF_MDW
function px2char(px) { return (((px - 5)/MDW * 100 + 0.5)|0)/100 }

/**
 * Provided by the jx-xlxs project in their starter code. (Left as is)
 * @param {string} str
 * @returns {ArrayBuffer}
 */
function stringToArrayBuffer (str) {
	var buf = new ArrayBuffer(str.length)
	var view = new Uint8Array(buf)
	for (var i = 0; i != str.length; ++i) {
		view[i] = str.charCodeAt(i) & 0xFF
	}
	return buf
}

/**
 * See {encodeMomentAsExcelDateNum} for further documentation
 *
 * @param date in yyyy-mm-dd format
 * @returns {number | string}
 */
function getExcelDateValue(date) {
	if (!date || _.isEmpty(_.trim(date))) return ''
	var isoDate = moment(date, momentHelper.isoDateFormat)
	return encodeMomentAsExcelDateNum(isoDate)
}

function getExcelTimeValue(time) {
	if (!time || _.isEmpty(_.trim(time))) return ''
	const parsedTime = momentHelper.parseIsoTime(time)
	return encodeMomentAsExcelDateNum(parsedTime)
}

function getExcelDateTimeValue(dateTime) {
	if (!dateTime || _.isEmpty(_.trim(dateTime))) return ''
	return encodeMomentAsExcelDateNum(moment(dateTime))
}

/**
 * Excel Date Rules:
 * All dates are stored as integers representing the number of days since January 1, 1900,
 * which is stored as number 1, to December 31, 9999 stored as 2958465
 *
 * Times are stored in Excel as decimals, between .0 and .99999,
 * that represent a proportion of the day where .0 is 00:00:00 and .99999 is 23:59:59.
 *
 * @return {Number} The excel representation of the input moment
 *
 * @param {Moment} momentToEncode
 */
function encodeMomentAsExcelDateNum(momentToEncode) {
	const start = moment('1900-01-01', momentHelper.isoDateFormat)
	const hours = momentToEncode.hours() / 24
	const minutes = momentToEncode.minutes() / (24 * 60)
	const seconds = momentToEncode.seconds() / (24 * 60 * 60)
	// Add 2 because of a Microsoft Bug:
	// https://support.microsoft.com/en-us/help/214326/excel-incorrectly-assumes-that-the-year-1900-is-a-leap-year
	return momentToEncode.diff(start, 'days') + 2 + hours + minutes + seconds
}

var charCodeA = 65

/**
 * Converts the column index to the Excel alpha value (A, AA, etc)
 * @param colIndex 0 based
 * @return {string}
 */
function columnToExcelAlpha(colIndex) {
	var dividend = colIndex
	var columnName = ''
	var modulo

	while (dividend > -1) {
		modulo = dividend % 26
		columnName = String.fromCharCode(charCodeA + modulo) + columnName
		dividend = ((dividend - modulo) / 26) - 1
	}

	return columnName
}

/**
 * This class has everything to do with creating an excel workbook.
 *
 * @class
 * @constructor
 */
var Workbook = function () {}

var borders = {
	// rgb(242,242,242)
	coloredCell: {style: 'thin', color: {rgb: 'FFF2F2F2'}},
	// rgb(128,128,128)
	totalVertical: {style: 'thin', color: {rgb: 'FF808080'}},
	// rgb(213,218,225)
	textCell: {style: 'thin', color: {rgb: 'FFD5DAE1'}},
	// rgb(227,227,227)
	headerCell: {style: 'thin', color: {rgb: 'FFE3E3E3'}}
}

// taken from ScoreColor.kt, we need to map the CSS color classes into an RGB value
// that accounts for shading and transparency (alpha channel) since the support
// for ARGB values in Excel is not working.
let _colors
function colors () {
	if (!_colors) {
		// Have to lazily initialize this because "constants" isn't populated
		// until the /globals XHR completes
		_colors = _.mapValues(constants.scoreColors, color => {
			return colorWithHoverColor(color.hexCode)
		})
	}

	return _colors
}

function colorWithHoverColor(base) {
	return base ? {
		base,
		hoverFont: shade(base, .5),
		hoverBackground: tint(base, .5)
	} : null
}

// conversions adopted from: //http://stackoverflow.com/a/5624139/5656782
function hexToRgb(hex) {
	var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
	return result ? {
		r: parseInt(result[1], 16),
		g: parseInt(result[2], 16),
		b: parseInt(result[3], 16)
	} : null
}
function rgbToHex(rgb) {
	return _.map(rgb, function (x) {
		var hex = Math.round(x).toString(16) // No rounding can result in NaN
		return hex.length === 1 ? '0' + hex : hex
	}).join('')
}
function shade(baseHexColor, shadeFactor) {
	var rgb = hexToRgb(baseHexColor)
	var newRGB = _.map(rgb, function(v) { return v * (1-shadeFactor) })
	return rgbToHex(newRGB)
}

function tint(baseHexColor, tintFactor) {
	var rgb = hexToRgb(baseHexColor)
	var newRGB = _.map(rgb, function(v) { return v + (255 - v) * tintFactor })
	return rgbToHex(newRGB)
}

/**
 * @returns {string} a locale specific date format string (i.e. 'MMM D, YYYY')
 */
function getDateFormatString() {
	if (locale().locale.startsWith('ar')) {
		// #50999 moment.localeData().longDateFormat() doesn't return what we
		// want for Arabic, so we'll just hardcode a format that's appropriate
		// for Excel
		return 'd\\ mmmm\\ yyyy'
	}

	let format = moment.localeData().longDateFormat(momentHelper.dateFormat)

	// #50999 moment.js surrounds literals with brackets: [de]
	// But Excel surrounds literals with quotes: "de"
	return format.replaceAll('[', '"').replaceAll(']', '"')
}

function getTimeFormatString(ourTimeFormatString) {
	const excelTimeFormat = ourTimeFormatString.startsWith('LT') ?
		moment.localeData().longDateFormat(ourTimeFormatString)
			.replace(" A", " AM/PM")
		: ourTimeFormatString
	return excelTimeFormat
}

function getDateTimeFormatString(ourTimeFormatString) {
	return `${getDateFormatString()} ${getTimeFormatString(ourTimeFormatString)}`
}

/**
 * Returns a prefix for Excel date formats that indicates the locale, which
 * Excel needs to localize things like the month and the character sets
 */
function getExcelLocalePrefix () {
	let l = locale().locale

	// For some reason, Excel doesn't respect 'ar' as a locale, but this value
	// is what Excel uses if you set it to Arabic in Excel itself
	let excelLocale = l.startsWith('ar') ? '20B0000' : l

	return `[$-${excelLocale}]`
}

function addHeaderRows(worksheet, label) {
	var rows = worksheet['!rows']
	_addHeaderCell(worksheet, rows, label, {height: 50, fontSize: 24})
}

function _addHeaderCell(worksheet, rows, value, opts) {
	var rowIndex = _.size(rows)

	rows.push({height: _.get(opts,'height',MIN_ROW_HEIGHT)})
	var cell = {v: value}
	var cellContext = {
		fontSize: _.get(opts,'fontSize',FONT_SIZE),
		verticalAlign: 'middle',
		wrap: false,
		borderless: true
	}
	_setCellStyle(cell, cellContext, worksheet['!merges'], rowIndex, 0)
	var cell_ref = XLSX.utils.encode_cell({c: 0, r: rowIndex})
	worksheet[cell_ref] = cell

	rows.push({})
}

function _setCellValue(cell, cellObject) {
	var value = cellObject.value
	if (cellObject.dataType !== TEXT && !isNaN(value) && !_.isEmpty(_.trim(value))) {
		value = _.toNumber(value)
	} else if (cellObject.dataType === DATE) {
		value = getExcelDateValue(cellObject.value)
	} else if (cellObject.dataType === TIME) {
		value = getExcelTimeValue(cellObject.value)
	} else if (cellObject.dataType === DATE_TIME) {
		value = getExcelDateTimeValue(cellObject.value)
	} else {
		// Even if a cell has no value it may have styles so we set everything every time
		// XLSX blows up if value is undefined though, so we set an empty string
		value = _.defaultTo(value,'')
		if (value === ' ') { // #28388
			value = ''
		}
	}

	// #51030 aggregation 'list-unique-values' may have an array as the value,
	// but Excel needs a single string
	if (_.isArray(value)) {
		value = value.join(', ')
	}

	cell.v = value
}

function _setCellDataType(cell) {
	if (typeof cell.v === 'number') cell.t = 'n'
	else if (typeof cell.v === 'boolean') cell.t = 'b'
	else {
		cell.t = 's'
	}
}
/**
 * Converts style attributes from cellObject onto the js-xlsx cell object
 *
 * @param {object} cell js-xlsx cell object
 * @param {object} cellObject our cell context object (extracted from the DOM)
 * @param {array} merges will be pushed onto this array
 * @param {number} colIndex 0 based
 * @param {number} rowIndex 0 based
 * @param {array} cols The worksheet columns (ws['!cols']) - this function will
 *     update the width attribute accordingly
 *
 * @private
 */
function _setCellStyle(cell, cellObject, merges, colIndex, rowIndex, cols) {
	var vAlign = cellObject.verticalAlign || 'top'
	cell.s = {
		alignment: {
			horizontal: cellObject.horizontalAlign || 'left',
			vertical:  vAlign === 'middle' ? 'center' : vAlign,
			wrapText: _.defaultTo(cellObject.wrap, true)
		},
		font: {
			sz: cellObject.fontSize || FONT_SIZE,
			bold: cellObject.fontWeight === 'bold' || cellObject.fontWeight >= 600
		}
	}

	// Cell Color
	// defined one of two ways, either a bgColor is specified which is a
	// a css class that references a predefined color, or the model on the
	// cellObject may define the color (as hex value) of the cell
	var color = colors()[cellObject.bgColor] || colorWithHoverColor(_.get(cellObject.model, 'color'))
	if (color) {
		// Only fill if we have a color or it will be black
		cell.s.fill = {
			// Not to be fooled, this is actually the cell background
			fgColor: {rgb: color.hoverBackground}
		}
		// FF is fully opaque
		cell.s.font.color = { rgb: 'FF' + color.hoverFont }
	}

	var formatContext = _getNumberFormat(cellObject)
	cell.s.numFmt = formatContext.format

	// Dimensions
	if (cols) {
		_setCellWidth(cols[colIndex], cellObject, formatContext, FONT_SCALE_FACTOR)
	}


	_applyMerges(cellObject, merges, colIndex, rowIndex)
	cell.s.border = _getBorder(cellObject)
}

/**
 * @param cellObject used to read data that is ready for display
 * @private
 */
function _getNumberFormat(cellObject) {
	let { value, dataType, precision, model } = cellObject

	if (_.isNil(precision)) {
		precision = dataType === CURRENCY ? config.defaultCurrencyPrecision : config.defaultPrecision
	}

	let format
	if (dataType === CURRENCY) {
		var curCode = cellObject.currencyCode || config.defaultCurrencyCode
		format = _buildCurrencyFormat(curCode, precision)
	} else if (dataType === DATE) {
		format = getExcelLocalePrefix() + getDateFormatString()
	} else if (dataType === TIME) {
		format = getExcelLocalePrefix()
			+ getTimeFormatString(cellObject.model.timeFormat)
	} else if (dataType === DATE_TIME) {
		format = getExcelLocalePrefix()
			+ getDateTimeFormatString(cellObject.model.timeFormat)
	} else {
		if (dataType === 'score') {
			dataType = config.showScoresAsPercent ? 'percent' : 'number'
		}

		var numberDecimalFormat = _.repeat('#', precision)
		if (dataType === NUMBER) {
			var wholeNumber = value % 1 === 0
			format = group(wholeNumber ? '###0' : '###0.' + numberDecimalFormat)
		} else if (dataType === PERCENT) {
			const percentValue = value * 100
			// https://stackoverflow.com/a/18358056/5656782
			const roundToPrecision = () => +(Math.round(percentValue + `e+${precision}`) + `e-${precision}`)
			const roundsToWholeNumber = () => roundToPrecision() % 1 === 0
			const wholePct = percentValue % 1 === 0 || roundsToWholeNumber()
			format = appendPercentSign(group(wholePct ? '###0' : '###0.' + numberDecimalFormat))
		}
	}
	return {
		format: format,
		precision: precision
	}
}

function _buildCurrencyFormat(currencyCode, precision) {
	var value = group('###0.' + _.repeat('0', precision))
	var formatOpts = { currencyCode: currencyCode }
	var currencyFmt = appendCurrencySymbol(value, formatOpts)

	// If multiple currencies use the same symbol it will be appended to the value
	// #12104 - the code must be placed in quotes for error will occur in excel
	currencyFmt = currencyFmt.replace(' '+currencyCode, ' "' + currencyCode + '"')

	// #35673 - reverse engineered by saving Excel and looking in the xl/styles.xml file
	// formatCode="_([$SAR]\ * #,##0.00_);_([$SAR]\ * \(#,##0.00\);_([$SAR]\ * &quot;-&quot;??_);_(@_)"
	currencyFmt = currencyFmt.replace('SAR', '[$SAR]\\')

	return currencyFmt
}

/**
 * Set the cell width based on the content type (number, currency, string)
 * and it's size, taking into account the format mask
 *
 * @param {object} column The sheetJS column object
 * @param {object} cellObject The scoreboard cell context object (from html-table-parser)
 * @param {string} formatContext.format
 * @param {number} formatContext.precision
 *
 * wch found in XLSX source: 67_wsxml.js (characters)
 * wpx found in XLSX source: 67_wsxml.js (pixels)
 *
 * @private
 */
function _setCellWidth(column, cellObject, formatContext, fontSizeFactor) {

	var factor =  fontSizeFactor || 1
	var dt = cellObject.dataType
	var v = cellObject.value
	const minCharWidth = () => Math.max(MIN_COLUMN_WIDTH_EXCEL_POINTS, px2char(column.wpx))

	// Don't base any size decisions off of empty cells
	if (_.size(cellObject.domValue) === 0) {
		return
	}

	// Base width off of text length (autofit) if dealing with a numeric value
	// because these are abbreviated in the UI and will be much wider in the export
	if (!isNaN(v) && (dt === CURRENCY || dt === NUMBER)) {

		var precision = formatContext.precision

		// Has grouping and precision but no currency symbols
		var valueSize = _.size(group(round(v, precision)))

		// Get count of currency symbols, codes, grouping symbols, and spaces
		var symbols = formatContext.format.replace(/#|"|0/g,'')

		// Number might not fill precision on screen, but it will in export
		var decimalPlacesInValue = (_.split(v, '.')[1] || []).length
		var precisionPad = Math.max(0, precision - decimalPlacesInValue)

		// Gets us really close, might be a couple chars longer due to duplicate
		// grouping symbols, but a reasonable compromise for lesser complexity
		var charLength = valueSize + _.size(symbols) + precisionPad

		column.wch = Math.max(column.wch || minCharWidth(column.wpx), (charLength * factor))
		delete column.wpx

		// console.log('value:', group(round(v, precision)), symbols, charLength, 'width (chars):', column.wch)

	} else if (dt === DATE) {
		if (!column.wch) {
			const headerWidth = px2char(column.wpx)
			const dateWidth = _.size(getDateFormatString()) + 2 // +2 for M -> MM and a little padding
			column.wch = Math.max(headerWidth, dateWidth)
			// console.log('value:', v, 'width (chars):', column.wch, momentHelper.dateFormatString())
			delete column.wpx
		}
	} else if (cellObject.colspan == 1) {
		// Expand width as long as textWidth doesn't push beyond the cell width
		const ceiling = Math.min(CELL_PADDING + cellObject.textWidth, CELL_PADDING + cellObject.width)
		const current = column.wpx || MIN_WIDTH_PIXELS
		column.wpx = Math.max(current, ceiling)
		// console.log('value:', v, ' cellObject.width:', cellObject.width, ' cellObject.textWidth:', cellObject.textWidth, ' width (px):', column.wpx)
	} else {
		// If a cell spans multiple cells we want to rely on the non-spanned cells
		// to dictate it's width.  BUT, it's possible that a header might span cells
		// that themselves do not have any content (e.g. report designer groupings)
		// In this case we must use the minimum width so it's not zero.
		column.wch = minCharWidth()
	}
}

function _getBorder(cellObject) {
	// Borders must be specified on every cell (even if merged)
	var cellBorder = cellObject.border || {}
	return cellObject.borderless ? {} : cellBorder
}

/**
 * Given a cellObject with a width set in pixels and a value, return the number of
 * pixels needed for height so that all text fits in the cell.
 *
 * @param {string} cellObject.value
 * @param {number} [column.wpx] column width in pixels
 * @param {number} [column.wch] column width in characters
 *
 * @private
 */
function _calculateCellHeight(cellObject, column, currentHeight) {
	var height = currentHeight
	const firstCell = _.result(cellObject, 'colIndex') == 0 && _.result(cellObject, 'colspan', 1) == 1
	const singleRow = _.result(cellObject, 'rowspan', 1) == 1
	const autoHeight = firstCell || singleRow

	const getFromCell = () => {
		// Getting height from rowspans will not evenly distribute the height
		if (singleRow) {
			var cellHeight = _.result(cellObject, 'height', 0)
			var cellHeightPoints = Math.max(MIN_ROW_HEIGHT, cellHeight * PIXEL_POINT_CONVERSION_FACTOR)
			// console.log('currentHeight:', currentHeight, 'newHeight:', height, 'cellHeight:', cellHeight, 'pts:', cellHeightPoints)
			return Math.max(cellHeightPoints, currentHeight)
		}
		return height
	}

	if (cellObject.autosize === false || !autoHeight) {
		height = getFromCell()
	} else if (_.get(column,'wpx')) { // If width is in `wch` assume it is wide enough since we autosized it already
		// Use the cellObject first because it accounts for colspans
		const {value, textWidth} = cellObject
		let linesNeeded = Math.ceil(textWidth / column.wpx)

		// Add additional lines when there are line breaks because they
		// only get counted as 1 character
		if (_.isString(value)) {
			linesNeeded += (value.match(/\n/g) || []).length
		}

		if (linesNeeded > 1) {
			var buffer = 1 // because words can cause an extra line break
			height = (linesNeeded + buffer) * ROW_HEIGHT_PER_LINE
			// console.log('value:', value, 'linesNeeded:', linesNeeded, 'text px:', textWidth)
		} else {
			height = getFromCell()
		}
	} else {
		height = getFromCell()
	}
	return height
}

/**
 * Create the worksheet merge objects (See documentation in file header, as well
 * as the unit tests) given the the cellObject's colspan and rowspan
 *
 * @param cellObject
 * @param merges An array that should have new merges pushed onto it
 * @param colIndex 0 based
 * @param rowIndex 0 based
 * @private
 */
function _applyMerges(cellObject, merges, colIndex, rowIndex) {

	var colLetter = columnToExcelAlpha(colIndex)
	var rowNum = rowIndex+1
	var start = colLetter + rowNum

	if (cellObject.colspan && cellObject.rowspan) {
		merges.push({
			s: start,
			e/* end */: columnToExcelAlpha(colIndex + cellObject.colspan - 1) + (rowIndex + cellObject.rowspan)
		})
	} else if (cellObject.colspan) {
		merges.push({
			s: start,
			e/* end */: columnToExcelAlpha(colIndex + cellObject.colspan - 1) + rowNum
		})
	} else if (cellObject.rowspan) {
		merges.push({
			s: start,
			e/* end */: colLetter + (rowIndex + cellObject.rowspan)
		})
	}
}

/**
 *
 * @param {Object[][]} data (rows and columns) where each cell is an object
 *     with expected properties. Must have atleast one row of data
 * @param {string} label Will be displayed in the first row of the spreadsheet
 */
Workbook.prototype.createSheet = function (data, label) {

	var worksheet = {}
	var merges = []
	var DATA_START_ROW = 2
	var rowCount = data.length + DATA_START_ROW
	var colCount = _.size(_.maxBy(data, _.size))
	var cols = worksheet['!cols'] = _.times(colCount, function () {
		return {}
	})
	// s=start, e=end, c=column, r=row
	var range = {s: {c: 0, r: 0}, e: {c: colCount-1, r: rowCount-1}}
	// worksheet['!merges']: array of range objects corresponding to the merged cells
	// in the worksheet. Plaintext utilities are unaware of merge cells. CSV
	// export will write all cells in the merge range if they exist, so be sure
	// that only the first cell (upper-left) in the range is set.
	worksheet['!merges'] = merges
	worksheet['!rows'] = []

	addHeaderRows(worksheet, label)

	for (var r = DATA_START_ROW; r < rowCount; ++r) {
		var sourceRow = data[r-DATA_START_ROW]
		for (var c = 0; c < _.size(sourceRow); ++c) {
			var cellObject = sourceRow[c] || {}
			if (cellObject) {
				var cell = {}
				_setCellValue(cell, cellObject)
				_setCellDataType(cell, cellObject)
				_setCellStyle(cell, cellObject, merges, c, r, cols)
				var cell_ref = XLSX.utils.encode_cell({c: c, r: r})
				worksheet[cell_ref] = cell
			}
		}
	}

	// Need to set the row heights after all cells are processed because if a column
	// has been autosized for a numeric format then we're good.
	for (var r = DATA_START_ROW; r < rowCount; ++r) {
		var row = {}
		// support for !rows is a js-xlsx-spider feature
		// based on https://github.com/protobi/js-xlsx/issues/25
		worksheet['!rows'].push(row)
		var height = 0
		var sourceRow = data[r - DATA_START_ROW]
		for (var c = 0; c < _.size(sourceRow); ++c) {
			var cellObject = sourceRow[c]
			var col = cols[c]
			var row = worksheet['!rows'][r]
			height = Math.max(height, _calculateCellHeight(cellObject, col, height))
		}
		row.height = height
	}
	worksheet['!ref'] = XLSX.utils.encode_range(range)
	return worksheet
}

Workbook.prototype.getAsArrayBuffer = function () {
	return [stringToArrayBuffer(XLSX.write(this, {bookType: 'xlsx', bookSST: true, type: 'binary'}))]
}

const Borders = borders
const applyMerges = _applyMerges
const setCellValue = _setCellValue
const setCellDataType = _setCellDataType
const setCellStyle = _setCellStyle
const setCellWidth = _setCellWidth
const calculateCellHeight = _calculateCellHeight

_.extend(Workbook, {
	Borders,
	ROW_HEIGHT_PER_LINE,
	CELL_PADDING,
	FONT_SIZE,
	columnToExcelAlpha,
	applyMerges,
	getExcelDateValue,
	setCellValue,
	setCellDataType,
	setCellStyle,
	setCellWidth,
	calculateCellHeight,
	shade,
	tint
})
export default Workbook
export { Borders }
export { ROW_HEIGHT_PER_LINE }
export { CELL_PADDING }
export { FONT_SIZE }
// So we can unit test each method independently
export { columnToExcelAlpha }
export { applyMerges }
export { getExcelDateValue }
export { setCellValue }
export { setCellDataType }
export { setCellStyle }
export { setCellWidth }
export { calculateCellHeight }
export { shade }
export { tint }


Workbook.__filename__ = 'components/export/excel-workbook.js'
